/**
 * O pool de eventos do painel (decisão 172; análise, seção 4.7).
 *
 * Nunca há um pedido por evento. O evento entra no pool (IndexedDB) e o pool envia em lote quando:
 * junta 50 eventos; o mais antigo espera há 60 s; a aba é escondida; o painel abre com sobras;
 * a rede volta. Só uma aba envia por vez (Web Locks). O evento só sai do pool quando o servidor
 * confirma o lote; falha de rede, 429 e 5xx mantêm tudo e tentam de novo com espera crescente;
 * 401 segura o lote até a mesma pessoa logar de novo; 413 divide o lote.
 *
 * O tempo ativo viaja no lote com um id (`batch.id`) que se repete nos reenvios, para o servidor
 * não somar o mesmo tempo duas vezes. Esse id e o tempo dele ficam guardados até a confirmação.
 */
import type { ActivityEnvelope, Outbox, OutboxRecord } from './outbox';
import type { ActivityBatchBody, ActivityBatchMeta, SendOutcome } from './sender';

export const POOL_LIMITS = {
  flushCount: 50,
  flushAgeMs: 60 * 1000,
  maxBatch: 100,
  maxRecords: 10_000,
  minBackoffMs: 30 * 1000,
  maxBackoffMs: 30 * 60 * 1000,
  /** O navegador corta pedidos `keepalive` acima de 64 KB; sobra folga para os headers. */
  keepaliveBytes: 60 * 1000,
  /** Lotes por envio, para uma aba não ficar presa esvaziando um pool grande. */
  roundsPerFlush: 5,
} as const;

const STATE_KEY = 'lm:activity:pool';

interface PoolState {
  /** Lote em aberto: o mesmo id e o mesmo tempo ativo até o servidor confirmar. */
  batch: { id: string; activeMs: Record<string, number> } | null;
  attempts: number;
  nextTryAt: number;
  /** Eventos descartados pelo teto desde o último envio confirmado. */
  dropped: number;
}

export type FlushReason = 'count' | 'age' | 'hidden' | 'start' | 'online' | 'manual';

export interface PoolDependencies {
  outbox: Outbox;
  send: (body: ActivityBatchBody, options: { keepalive?: boolean }) => Promise<SendOutcome>;
  getUserId: () => string | null;
  drainActive: () => Record<string, number>;
  newId: () => string;
  /** Avisado quando o teto descartou eventos, para virar o evento `activity.dropped`. */
  onDropped: (byName: Record<string, number>, total: number) => void;
  now?: () => number;
  random?: () => number;
  setTimer?: (fn: () => void, ms: number) => () => void;
  /** Roda `task` se ninguém estiver enviando; devolve `null` quando outra aba está. */
  lock?: <T>(task: () => Promise<T>) => Promise<T | null>;
  storage?: Storage | null;
}

function browserLock<T>(task: () => Promise<T>): Promise<T | null> {
  const locks = typeof navigator === 'undefined' ? undefined : navigator.locks;
  if (!locks) return task();
  return locks.request(
    'lm-activity-flush',
    { ifAvailable: true },
    async (lock): Promise<T | null> => (lock ? task() : null),
  ) as Promise<T | null>;
}

function emptyState(): PoolState {
  return { batch: null, attempts: 0, nextTryAt: 0, dropped: 0 };
}

export class ActivityPool {
  private readonly now: () => number;
  private readonly random: () => number;
  private readonly setTimer: (fn: () => void, ms: number) => () => void;
  private readonly lock: <T>(task: () => Promise<T>) => Promise<T | null>;
  private readonly storage: Storage | null;
  /** Cópia em memória do que esta aba gravou e ainda não foi confirmado (envio de saída). */
  private readonly unsent = new Map<string, OutboxRecord>();
  private memoryState: PoolState = emptyState();
  private addedSinceFlush = 0;
  private addedSinceCap = 0;
  private cancelAgeTimer: (() => void) | null = null;
  private batchSize: number = POOL_LIMITS.maxBatch;
  private flushing: Promise<void> | null = null;

  constructor(private readonly deps: PoolDependencies) {
    this.now = deps.now ?? (() => Date.now());
    this.random = deps.random ?? Math.random;
    this.setTimer =
      deps.setTimer ??
      ((fn, ms) => {
        const id = setTimeout(fn, ms);
        return () => clearTimeout(id);
      });
    this.lock = deps.lock ?? browserLock;
    this.storage =
      deps.storage === undefined
        ? typeof localStorage === 'undefined'
          ? null
          : localStorage
        : deps.storage;
  }

  /** Grava no pool e decide se já é hora de enviar. Nunca lança. */
  async add(record: OutboxRecord): Promise<void> {
    try {
      await this.deps.outbox.put([record]);
    } catch {
      return;
    }
    this.unsent.set(record.eventId, record);
    this.addedSinceFlush += 1;
    this.addedSinceCap += 1;
    if (this.addedSinceCap >= POOL_LIMITS.flushCount) {
      this.addedSinceCap = 0;
      await this.enforceCap();
    }
    if (this.addedSinceFlush >= POOL_LIMITS.flushCount) {
      void this.flush('count');
    } else if (!this.cancelAgeTimer) {
      this.cancelAgeTimer = this.setTimer(() => {
        this.cancelAgeTimer = null;
        void this.flush('age');
      }, POOL_LIMITS.flushAgeMs);
    }
  }

  /** Envia o que houver. Um envio por vez nesta aba; entre abas, a trava decide. */
  flush(reason: FlushReason = 'manual'): Promise<void> {
    if (this.flushing) return this.flushing;
    const run = this.lock(() => this.flushLocked(reason))
      .then(() => undefined)
      .catch(() => undefined)
      .finally(() => {
        this.flushing = null;
      });
    this.flushing = run;
    return run;
  }

  /**
   * Saída da página (`pagehide`): manda o que esta aba gravou e ainda não foi confirmado com
   * `keepalive`, sem ler o IndexedDB (a página pode acabar antes). Se a resposta não chegar, os
   * eventos continuam no pool e voltam no próximo envio; o servidor descarta os repetidos.
   */
  flushOnExit(): void {
    const userId = this.deps.getUserId();
    if (!userId) return;
    const records = Array.from(this.unsent.values())
      .filter((record) => record.userId === userId)
      .sort((a, b) => a.createdAt - b.createdAt || a.envelope.seq - b.envelope.seq);
    const meta = this.openBatch(records.length > 0);
    if (!meta) return;
    const events: ActivityEnvelope[] = [];
    let bytes = 200 + JSON.stringify(meta).length;
    for (const record of records.slice(0, POOL_LIMITS.maxBatch)) {
      const size = JSON.stringify(record.envelope).length + 1;
      if (bytes + size > POOL_LIMITS.keepaliveBytes) break;
      bytes += size;
      events.push(record.envelope);
    }
    void this.deps.send({ batch: meta, events }, { keepalive: true }).then((outcome) => {
      if (outcome.kind !== 'sent') return;
      this.confirm(events.map((event) => event.eventId));
      void this.deps.outbox.remove(events.map((event) => event.eventId)).catch(() => undefined);
    });
  }

  /** A rede voltou: zera a espera e envia. */
  online(): Promise<void> {
    const state = this.readState();
    this.writeState({ ...state, attempts: 0, nextTryAt: 0 });
    return this.flush('online');
  }

  private async flushLocked(reason: FlushReason): Promise<void> {
    if (this.cancelAgeTimer) {
      this.cancelAgeTimer();
      this.cancelAgeTimer = null;
    }
    this.addedSinceFlush = 0;
    const state = this.readState();
    if (state.nextTryAt > this.now() && reason !== 'online') {
      this.scheduleRetry(state.nextTryAt - this.now());
      return;
    }
    const userId = this.deps.getUserId();
    if (!userId) return;

    for (let round = 0; round < POOL_LIMITS.roundsPerFlush; round += 1) {
      const records = await this.deps.outbox.take(userId, this.batchSize);
      const meta = this.openBatch(records.length > 0);
      if (!meta) return;
      meta.pool = await this.poolState();
      const outcome = await this.deps.send(
        { batch: meta, events: records.map((record) => record.envelope) },
        {},
      );

      if (outcome.kind === 'sent') {
        const ids = records.map((record) => record.eventId);
        await this.deps.outbox.remove(ids);
        this.confirm(ids);
        this.batchSize = POOL_LIMITS.maxBatch;
        if (records.length < POOL_LIMITS.maxBatch) return;
        continue;
      }
      if (outcome.kind === 'split' && this.batchSize > 1) {
        this.batchSize = Math.max(1, Math.ceil(this.batchSize / 2));
        continue;
      }
      this.backoff(
        outcome.kind === 'held'
          ? POOL_LIMITS.maxBackoffMs
          : outcome.kind === 'retry'
            ? outcome.retryAfterMs
            : undefined,
      );
      return;
    }
    // Ainda há eventos: continua logo em seguida, sem prender esta aba.
    this.cancelAgeTimer = this.setTimer(() => {
      this.cancelAgeTimer = null;
      void this.flush('count');
    }, 1000);
  }

  /**
   * O lote em aberto, ou um novo com o tempo ativo acumulado. `null` quando não há nada a mandar.
   */
  private openBatch(hasEvents: boolean): ActivityBatchMeta | null {
    const state = this.readState();
    let batch = state.batch;
    if (!batch) {
      const activeMs = this.deps.drainActive();
      if (!hasEvents && Object.keys(activeMs).length === 0) return null;
      batch = { id: this.deps.newId(), activeMs };
      this.writeState({ ...state, batch });
    }
    return {
      id: batch.id,
      ...(Object.keys(batch.activeMs).length ? { activeMs: { ...batch.activeMs } } : {}),
    };
  }

  private confirm(ids: string[]): void {
    for (const id of ids) this.unsent.delete(id);
    this.writeState(emptyState());
  }

  private backoff(retryAfterMs?: number): void {
    const state = this.readState();
    const attempts = state.attempts + 1;
    const base = Math.min(POOL_LIMITS.maxBackoffMs, POOL_LIMITS.minBackoffMs * 2 ** (attempts - 1));
    // Um pouco de sorteio: navegadores que falharam juntos não voltam todos no mesmo segundo.
    const jittered = Math.round(base * (0.8 + this.random() * 0.4));
    const wait = Math.min(POOL_LIMITS.maxBackoffMs, Math.max(retryAfterMs ?? 0, jittered));
    this.writeState({ ...state, attempts, nextTryAt: this.now() + wait });
    this.scheduleRetry(wait);
  }

  private scheduleRetry(ms: number): void {
    if (this.cancelAgeTimer) this.cancelAgeTimer();
    this.cancelAgeTimer = this.setTimer(
      () => {
        this.cancelAgeTimer = null;
        void this.flush('age');
      },
      Math.max(1000, ms),
    );
  }

  private async enforceCap(): Promise<void> {
    try {
      const count = await this.deps.outbox.count();
      if (count <= POOL_LIMITS.maxRecords) return;
      const byName = await this.deps.outbox.trim(POOL_LIMITS.maxRecords);
      const total = Object.values(byName).reduce((sum, value) => sum + value, 0);
      if (total <= 0) return;
      const state = this.readState();
      this.writeState({ ...state, dropped: state.dropped + total });
      this.deps.onDropped(byName, total);
    } catch {
      /* o teto é proteção; uma falha aqui não impede gravar nem enviar */
    }
  }

  private async poolState(): Promise<NonNullable<ActivityBatchMeta['pool']>> {
    const state = this.readState();
    let pending = 0;
    let oldestAgeMs = 0;
    try {
      pending = await this.deps.outbox.count();
      const oldest = await this.deps.outbox.oldest();
      if (oldest !== null) oldestAgeMs = Math.max(0, Math.round(this.now() - oldest));
    } catch {
      /* estado do pool é informativo */
    }
    return { pending, oldestAgeMs, retries: state.attempts, dropped: state.dropped };
  }

  private readState(): PoolState {
    if (!this.storage) return this.memoryState;
    try {
      const raw = this.storage.getItem(STATE_KEY);
      if (!raw) return this.memoryState;
      const parsed = JSON.parse(raw) as Partial<PoolState>;
      return {
        batch:
          parsed.batch && typeof parsed.batch.id === 'string'
            ? { id: parsed.batch.id, activeMs: parsed.batch.activeMs ?? {} }
            : null,
        attempts: Number(parsed.attempts ?? 0) || 0,
        nextTryAt: Number(parsed.nextTryAt ?? 0) || 0,
        dropped: Number(parsed.dropped ?? 0) || 0,
      };
    } catch {
      return this.memoryState;
    }
  }

  private writeState(state: PoolState): void {
    this.memoryState = state;
    if (!this.storage) return;
    try {
      this.storage.setItem(STATE_KEY, JSON.stringify(state));
    } catch {
      /* fica em memória nesta aba */
    }
  }
}
